import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, inr, fmtDate, today } from "@/lib/api";
import { Field, TableWrap, Empty, th, td, tr } from "@/components/common";
import { DocUpload, nativeInput } from "@/components/market";

export const DESIGNATIONS = ["Office Staff", "Accountant", "Manager", "Driver", "Helper", "Mechanic"];
export const ATT = { P: "Present", A: "Absent", HD: "Half-Day", PL: "Paid Leave", UL: "Unpaid Leave" };
export const thisMonth = () => today().slice(0, 7);
const EMPTY = { name: "", designation: "Office Staff", phone: "", doj: "", basic: "", hra: "", bank_name: "", account_holder: "", account_number: "", ifsc: "",
  pf_enabled: false, uan: "", esi_enabled: false, esic_no: "", ot_rate: "", late_rate: "", aadhaar_file_id: null, pan_file_id: null, photo_file_id: null, bank_proof_file_id: null, active: true };
const NUMS = ["basic", "hra", "ot_rate", "late_rate"];

function Toggle({ label, checked, onChange, testid }) {
  return <label className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"><span>{label}</span><Switch data-testid={testid} checked={!!checked} onCheckedChange={onChange} /></label>;
}

export function StaffDialog({ open, initial, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF(initial ? { ...EMPTY, ...initial } : EMPTY); }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const inp = (k, tid, extra = {}) => <Input data-testid={tid} value={f[k] ?? ""} onChange={set(k)} {...extra} />;
  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, ...Object.fromEntries(NUMS.map((k) => [k, Number(f[k]) || 0])) };
    try { initial ? await api.put(`/hr/staff/${initial.id}`, body) : await api.post("/hr/staff", body); toast.success("Staff saved"); onSaved(); onClose(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto bg-card" data-testid="staff-dialog">
        <DialogHeader><DialogTitle>{initial ? "Edit Staff" : "Add Staff"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-3 sm:grid-cols-3">
          <Field label="Employee Name *">{inp("name", "staff-name-input", { required: true })}</Field>
          <Field label="Designation"><select data-testid="staff-designation-select" className={nativeInput} value={f.designation} onChange={set("designation")}>{DESIGNATIONS.map((d) => <option key={d}>{d}</option>)}</select></Field>
          <Field label="Mobile Number">{inp("phone", "staff-phone-input", { maxLength: 10, inputMode: "numeric" })}</Field>
          <Field label="Date of Joining">{inp("doj", "staff-doj-input", { type: "date" })}</Field>
          <Field label="Basic Salary / month (₹)">{inp("basic", "staff-basic-input", { type: "number", min: 0 })}</Field>
          <Field label="HRA / Allowance / month (₹)">{inp("hra", "staff-hra-input", { type: "number", min: 0 })}</Field>
          <Field label="Overtime Rate (₹/hour)">{inp("ot_rate", "staff-ot-input", { type: "number", min: 0 })}</Field>
          <Field label="Late Deduction (₹/hour)">{inp("late_rate", "staff-late-input", { type: "number", min: 0 })}</Field>
          <div className="flex items-end"><Toggle label="Active" checked={f.active} onChange={set("active")} testid="staff-active-switch" /></div>
          <Field label="Bank Name">{inp("bank_name", "staff-bank-input")}</Field>
          <Field label="Account Holder">{inp("account_holder", "staff-holder-input")}</Field>
          <Field label="Account Number">{inp("account_number", "staff-account-input")}</Field>
          <Field label="IFSC">{inp("ifsc", "staff-ifsc-input", { maxLength: 11, className: "uppercase" })}</Field>
          <Toggle label="PF (12%)" checked={f.pf_enabled} onChange={set("pf_enabled")} testid="staff-pf-switch" />
          <Field label="UAN Number">{inp("uan", "staff-uan-input")}</Field>
          <Toggle label="ESI (0.75% / 3.25%)" checked={f.esi_enabled} onChange={set("esi_enabled")} testid="staff-esi-switch" />
          <Field label="ESIC Number">{inp("esic_no", "staff-esic-input")}</Field>
          <div />
          {[["aadhaar_file_id", "Aadhaar Copy"], ["pan_file_id", "PAN Card"], ["photo_file_id", "Photo"], ["bank_proof_file_id", "Bank Proof"]].map(([k, l]) => (
            <Field key={k} label={l}><DocUpload label={l} refType="staff_doc" value={f[k]} onChange={set(k)} testid={`staff-${k}`} /></Field>
          ))}
          <DialogFooter className="sm:col-span-3"><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" data-testid="staff-save-btn">Save Staff</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function StaffTab({ staff, reload }) {
  const [dlg, setDlg] = useState(null);
  const del = async (s) => { if (!window.confirm(`Delete ${s.name}?`)) return; try { await api.delete(`/hr/staff/${s.id}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <>
      <div className="mb-3 flex justify-end"><Button data-testid="add-staff-btn" onClick={() => setDlg({})}><Plus className="mr-1 h-4 w-4" />Add Staff</Button></div>
      <TableWrap>
        <thead><tr>{["Employee", "Designation", "Mobile", "Joining", "Basic + HRA", "PF / ESI", "Advance Bal.", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>{staff.map((s) => (
          <tr key={s.id} className={tr} data-testid={`staff-row-${s.id}`}>
            <td className={td}><div className="font-semibold">{s.name}</div>{!s.active && <span className="text-[10px] text-rose-600">Inactive</span>}</td>
            <td className={td}>{s.designation}</td><td className={td}>{s.phone || "-"}</td><td className={td}>{s.doj ? fmtDate(s.doj) : "-"}</td>
            <td className={`${td} amount`}>{inr(s.basic)} + {inr(s.hra)}</td>
            <td className={td}>{s.pf_enabled ? "PF" : "-"} / {s.esi_enabled ? "ESI" : "-"}</td>
            <td className={`${td} amount`}>{inr(s.advance_balance || 0)}</td>
            <td className={td}><button data-testid={`edit-staff-${s.id}`} onClick={() => setDlg(s)} className="mr-2"><Pencil className="h-4 w-4" /></button><button data-testid={`delete-staff-${s.id}`} onClick={() => del(s)}><Trash2 className="h-4 w-4 text-rose-600" /></button></td>
          </tr>))}</tbody>
      </TableWrap>
      {!staff.length && <Empty testid="staff-empty" text="No staff added yet" />}
      <StaffDialog open={!!dlg} initial={dlg?.id ? dlg : null} onClose={() => setDlg(null)} onSaved={reload} />
    </>
  );
}

export function AttendanceTab({ staff }) {
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState({});
  const [month, setMonth] = useState(null);
  const load = () => {
    api.get(`/hr/attendance?date=${date}`).then((r) => setRows(Object.fromEntries(r.data.map((a) => [a.staff_id, a])))).catch((e) => toast.error(errMsg(e)));
    api.get(`/hr/attendance/month?month=${date.slice(0, 7)}`).then((r) => setMonth(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps
  const active = staff.filter((s) => s.active);
  const setRow = (id, k, v) => setRows({ ...rows, [id]: { status: "P", ot_hours: 0, late_hours: 0, ...rows[id], [k]: v } });
  const save = async () => {
    const entries = active.map((s) => ({ staff_id: s.id, status: rows[s.id]?.status || "P", ot_hours: Number(rows[s.id]?.ot_hours) || 0, late_hours: Number(rows[s.id]?.late_hours) || 0 }));
    try { await api.put("/hr/attendance", { date, entries }); toast.success("Attendance saved"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <Field label="Date"><Input type="date" max={today()} data-testid="att-date-input" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Button data-testid="att-save-btn" onClick={save} disabled={!active.length}>Save Attendance</Button>
        <span className="text-xs text-muted-foreground">Bina chune sab "Present" save honge.</span>
      </div>
      <TableWrap>
        <thead><tr>{["Employee", "Status", "OT Hours", "Late Hours"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>{active.map((s) => (
          <tr key={s.id} className={tr}>
            <td className={td}>{s.name} <span className="text-xs text-muted-foreground">{s.designation}</span></td>
            <td className={td}><div className="flex flex-wrap gap-1">{Object.entries(ATT).map(([k, l]) => (
              <button type="button" key={k} data-testid={`att-${s.id}-${k}`} onClick={() => setRow(s.id, "status", k)}
                className={`rounded border px-2 py-1 text-xs ${(rows[s.id]?.status || "P") === k ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"}`}>{l}</button>))}</div></td>
            <td className={td}><Input type="number" min="0" step="0.5" className="h-8 w-20" data-testid={`att-ot-${s.id}`} value={rows[s.id]?.ot_hours ?? ""} onChange={(e) => setRow(s.id, "ot_hours", e.target.value)} /></td>
            <td className={td}><Input type="number" min="0" step="0.5" className="h-8 w-20" data-testid={`att-late-${s.id}`} value={rows[s.id]?.late_hours ?? ""} onChange={(e) => setRow(s.id, "late_hours", e.target.value)} /></td>
          </tr>))}</tbody>
      </TableWrap>
      {month && <MonthSummary m={month} staff={active} />}
    </>
  );
}

const DOT = { P: "bg-emerald-500", A: "bg-rose-500", HD: "bg-amber-400", PL: "bg-sky-500", UL: "bg-slate-400" };

function MonthSummary({ m, staff }) {
  const days = Array.from({ length: m.days_in_month }, (_, i) => String(i + 1).padStart(2, "0"));
  return (
    <div className="mt-6" data-testid="att-month-summary">
      <h3 className="mb-2 font-semibold">Monthly Summary — {m.month} ({m.days_in_month} days)</h3>
      <div className="mb-2 flex flex-wrap gap-3 text-[11px]">{Object.entries(ATT).map(([k, l]) => <span key={k} className="flex items-center gap-1"><span className={`h-2.5 w-2.5 rounded-sm ${DOT[k]}`} />{l}</span>)}</div>
      <TableWrap>
        <thead><tr><th className={th}>Employee</th>{days.map((d) => <th key={d} className="px-0.5 text-[10px]">{Number(d)}</th>)}{["P", "A", "HD", "PL", "UL", "Payable"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>{staff.map((s) => {
          const x = m.staff[s.id] || { days: {}, payable: 0 };
          return (
            <tr key={s.id} className={tr} data-testid={`att-summary-${s.id}`}>
              <td className={td}>{s.name}</td>
              {days.map((d) => <td key={d} className="px-0.5"><span title={ATT[x.days[d]] || "Not marked"} className={`block h-3 w-3 rounded-sm ${DOT[x.days[d]] || "bg-secondary"}`} /></td>)}
              {["P", "A", "HD", "PL", "UL"].map((k) => <td key={k} className={td}>{x[k] || 0}</td>)}
              <td className={`${td} font-semibold`} data-testid={`att-payable-${s.id}`}>{x.payable}</td>
            </tr>);
        })}</tbody>
      </TableWrap>
    </div>
  );
}

function EntryDialog({ kind, initial, staff, onClose, onSaved }) {
  const adv = kind === "advances";
  const [f, setF] = useState({});
  useEffect(() => { setF(initial?.id ? initial : adv ? { staff_id: "", date: today(), amount: "", mode: "Cash", remarks: "" } : { staff_id: "", month: thisMonth(), kind: "Festival Bonus", amount: "", remarks: "" }); }, [initial, adv]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try { const body = { ...f, amount: Number(f.amount) || 0 }; initial?.id ? await api.put(`/hr/${kind}/${initial.id}`, body) : await api.post(`/hr/${kind}`, body); toast.success("Saved"); onSaved(); onClose(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-card" data-testid={`${kind}-dialog`}>
        <DialogHeader><DialogTitle>{adv ? "Salary Advance" : "Bonus / Incentive"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-3">
          <Field label="Employee *"><select required data-testid={`${kind}-staff-select`} className={nativeInput} value={f.staff_id || ""} onChange={set("staff_id")}><option value="">— Select —</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Field>
          {adv ? <Field label="Payment Date *"><Input type="date" required data-testid="adv-date-input" value={f.date || ""} onChange={set("date")} /></Field>
            : <Field label="Salary Month *"><Input type="month" required data-testid="bonus-month-input" value={f.month || ""} onChange={set("month")} /></Field>}
          <Field label="Amount (₹) *"><Input type="number" min="1" required data-testid={`${kind}-amount-input`} value={f.amount ?? ""} onChange={set("amount")} /></Field>
          {adv ? <Field label="Mode"><select data-testid="adv-mode-select" className={nativeInput} value={f.mode} onChange={set("mode")}>{["Cash", "UPI", "Bank"].map((m) => <option key={m}>{m}</option>)}</select></Field>
            : <Field label="Type"><select data-testid="bonus-kind-select" className={nativeInput} value={f.kind} onChange={set("kind")}>{["Festival Bonus", "Diwali Bonus", "Performance Incentive", "Trip Bonus", "Other"].map((m) => <option key={m}>{m}</option>)}</select></Field>}
          <Field label="Remarks"><Input data-testid={`${kind}-remarks-input`} value={f.remarks || ""} onChange={set("remarks")} /></Field>
          <DialogFooter><Button type="submit" data-testid={`${kind}-save-btn`}>Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function MoneyTab({ staff, reload }) {
  const [adv, setAdv] = useState([]);
  const [bon, setBon] = useState([]);
  const [dlg, setDlg] = useState(null);
  const name = (id) => staff.find((s) => s.id === id)?.name || "-";
  const load = () => { api.get("/hr/advances").then((r) => setAdv(r.data)); api.get("/hr/bonuses").then((r) => setBon(r.data)); reload(); };
  useEffect(() => { api.get("/hr/advances").then((r) => setAdv(r.data)); api.get("/hr/bonuses").then((r) => setBon(r.data)); }, []);
  const del = async (kind, x) => { if (!window.confirm("Delete this entry?")) return; try { await api.delete(`/hr/${kind}/${x.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const list = (kind, items, cols, cells) => (
    <div className="min-w-0">
      <div className="mb-2 flex items-center justify-between"><h3 className="font-semibold">{kind === "advances" ? "Salary Advances" : "Bonus & Incentives"}</h3>
        <Button size="sm" data-testid={`add-${kind}-btn`} onClick={() => setDlg({ kind })}><Plus className="mr-1 h-4 w-4" />Add</Button></div>
      <TableWrap>
        <thead><tr>{[...cols, ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>{items.map((x) => <tr key={x.id} className={tr} data-testid={`${kind}-row-${x.id}`}>{cells(x).map((c, i) => <td key={i} className={td}>{c}</td>)}
          <td className={td}><button onClick={() => setDlg({ kind, initial: x })} className="mr-2" data-testid={`edit-${kind}-${x.id}`}><Pencil className="h-4 w-4" /></button><button onClick={() => del(kind, x)} data-testid={`delete-${kind}-${x.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></button></td></tr>)}</tbody>
      </TableWrap>
      {!items.length && <Empty text="No entries" />}
    </div>
  );
  return (
    <div className="grid gap-6 xl:grid-cols-2">
      {list("advances", adv, ["Date", "Employee", "Amount", "Mode", "Remarks"], (x) => [fmtDate(x.date), name(x.staff_id), inr(x.amount), x.mode, x.remarks])}
      {list("bonuses", bon, ["Month", "Employee", "Type", "Amount", "Remarks"], (x) => [x.month, name(x.staff_id), x.kind, inr(x.amount), x.remarks])}
      {dlg && <EntryDialog kind={dlg.kind} initial={dlg.initial} staff={staff.filter((s) => s.active)} onClose={() => setDlg(null)} onSaved={load} />}
    </div>
  );
}
